"use client"

import * as React from "react"
import { ScanSearch } from "lucide-react"
import { useLocation, useNavigate } from "react-router-dom"

import { Button } from "@ibada/ui"

import { useActiveBranchSlug, useActiveWorkspaceSlug } from "@/features/auth"
import {
  buildFinanceInvestigationHref,
  type InvestigationContext,
} from "@/features/finance/lib/finance-investigation-url"
import {
  investigationRecentFromContext,
  investigationRecentsScopeKey,
  useInvestigationRecentsStore,
} from "@/features/finance/stores/investigation-recents-store"
import { AnalyticsEvents, captureEvent } from "@/shared/lib/analytics/analytics"
import { useTranslations } from "@/i18n"

/**
 * Navigate into the Finance Investigation surface for a given context. This is the single primary entry
 * point wired across Finance (transaction, receipt, journal, ledger, trial balance, fund, budget,
 * reconciliation): a single object opens the workspace; a scope opens the prefiltered Explorer.
 */
export function useInvestigate() {
  const navigate = useNavigate()
  const workspaceSlug = useActiveWorkspaceSlug()
  const branchSlug = useActiveBranchSlug()
  const { search } = useLocation()
  const financeContext =
    new URLSearchParams(search).get("context") === "parish" ? ("parish" as const) : ("branch" as const)
  const pushRecent = useInvestigationRecentsStore((state) => state.pushRecent)

  return React.useCallback(
    (context: InvestigationContext) => {
      const href = buildFinanceInvestigationHref(context, { workspaceSlug, branchSlug, financeContext })
      if (href === "/dashboard") return
      /*
       * The single entry point to the investigation workspace, so one call here covers every surface that
       * offers it. `entity_kind` is WHICH kind of object was investigated (transaction, journal, fund…),
       * derived from which context key is set, never the id itself, so this stays a low-cardinality answer to
       * "does anyone actually use the audit trail, and from where?".
       */
      captureEvent(AnalyticsEvents.InvestigationOpened, {
        entity_kind: Object.keys(context).find((key) => key !== "label") ?? "unknown",
        finance_context: financeContext,
      })
      // Remembered for the launcher's "Recent investigations" (this device, this workspace + branch only).
      const scopeKey = investigationRecentsScopeKey(workspaceSlug, branchSlug)
      if (scopeKey) pushRecent(scopeKey, investigationRecentFromContext(context, href))
      navigate(href)
    },
    [navigate, workspaceSlug, branchSlug, financeContext, pushRecent],
  )
}

type InvestigateButtonProps = {
  context: InvestigationContext
  label?: string
  iconOnly?: boolean
} & Pick<React.ComponentProps<typeof Button>, "variant" | "size" | "className">

/** Shared "Investigate" button. Stops propagation so it can sit inside clickable rows. */
export function InvestigateButton({
  context,
  label,
  iconOnly = false,
  variant = "ghost",
  size = "sm",
  className,
}: InvestigateButtonProps) {
  const investigate = useInvestigate()
  const t = useTranslations("finance.audit")
  const text = label ?? t("investigate")

  return (
    <Button
      variant={variant}
      size={size}
      className={className}
      onClick={(event) => {
        event.stopPropagation()
        investigate(context)
      }}
      title={text}
    >
      <ScanSearch className={iconOnly ? "size-4" : "size-4"} />
      {iconOnly ? <span className='sr-only'>{text}</span> : text}
    </Button>
  )
}
